import CTALink from "@/components/CTA/variants/CtaLink/client";
import { FormatSubType, FormatType } from "@/constants/tagular/main";
import {
  HOMEPAGE_HERO_DARK_BG,
  HOMEPAGE_HERO_LIGHT_BG,
} from "@/components/Homepage/HomepageHero/constants";
import type { HomepageHeroClientProps } from "@/components/Homepage/HomepageHero/types";
import HomepageHeroBackgroundImage from "@/components/Homepage/HomepageHero/components/HomepageHeroBackgroundImage";
import HomepageHeroHeading from "@/components/Homepage/HomepageHero/components/HomepageHeroHeading";
import ProductCardList from "@/components/ProductCardList";
import Tooltip from "@/components/Tooltip";
import Icon from "@/components/icons";
import sanitizeAndParseInlineHTML from "@/utils/functions/sanitizeAndParseInlineHTML";
import SearchBox from "@/components/ProductSearch/SearchBox";
import ProductSearch from "@/components/ProductSearch";
import { cn } from "@/lib/utils";

export default function BaseHomepageHeroMerchandising({
  title,
  titleColor,
  descriptionItems,
  descriptionColor,
  primaryCta,
  secondaryCta,
  desktopImage,
  tabletImage,
  mobileImage,
  theme = "dark",
  products = [],
  eventing,
  tooltip,
  headingLevel,
  className = "",
  search,
  onFocus,
  onBlur,
  isCarouselSlide = false,
}: HomepageHeroClientProps) {
  const bgFallback = !desktopImage
    ? theme === "light"
      ? HOMEPAGE_HERO_LIGHT_BG
      : HOMEPAGE_HERO_DARK_BG
    : "";
  return (
    <div
      className={cn(
        "relative pt-14 sm:pt-10 lg:px-20 xl:py-20 2xl:px-32 mx-break-out",
        bgFallback,
        className,
      )}
    >
      {/* Background Image */}
      {desktopImage && (
        <>
          <HomepageHeroBackgroundImage
            imageSrc={mobileImage ?? tabletImage ?? desktopImage}
            screenSize="mobile"
            theme={theme}
          />
          <HomepageHeroBackgroundImage
            imageSrc={tabletImage ?? desktopImage}
            screenSize="tablet"
            theme={theme}
          />
          <HomepageHeroBackgroundImage
            imageSrc={desktopImage}
            screenSize="desktop"
            theme={theme}
          />
        </>
      )}

      <div className="container flex flex-col justify-between gap-10 xl:max-w-screen-2xl xl:flex-row xl:items-center">
        {/* Title, Description, CTAs */}
        <div className="xl:basis-3/5">
          <HomepageHeroHeading
            title={title}
            titleColor={titleColor}
            descriptionItems={descriptionItems}
            descriptionColor={descriptionColor}
            headingLevel={headingLevel}
          />
          <div className="flex flex-col sm:flex-row gap-4">
            {search && (
              <ProductSearch
                searchButtonColor={search?.searchButtonColor}
                searchButtonLabelColor={search?.searchButtonLabelColor}
                placeholder={search?.placeholder ?? "Search our 4000+ courses"}
                button={search?.searchButtonLabel ?? "Search"}
                onFocus={onFocus}
                onBlur={onBlur}
              />
            )}

            {primaryCta && !search && (
              <CTALink
                className="w-full sm:w-auto"
                type="brand"
                size="xl"
                url={primaryCta.url}
                label={primaryCta.label}
                html_id={eventing?.primaryCtaHtmlId}
                name={eventing?.primaryCtaName}
                location={eventing?.sectionLocation ?? "hero"}
              />
            )}
            {secondaryCta && (
              <CTALink
                className="w-full sm:w-auto"
                type="brandOutline"
                size="xl"
                url={secondaryCta.url}
                label={secondaryCta.label}
                html_id={eventing?.secondaryCtaHtmlId}
                name={eventing?.secondaryCtaName}
                location={eventing?.sectionLocation ?? "hero"}
              />
            )}
          </div>
          {!search && tooltip && tooltip.enabled && (
            <div className="flex mt-8">
              <div
                className="flex items-center elm-p3 font-normal space-x-1"
                style={{ color: descriptionColor }}
              >
                <span>{tooltip.label}</span>
                <Tooltip
                  content={sanitizeAndParseInlineHTML(tooltip.content)}
                  className="elm-p4"
                >
                  <Icon type="info" svgProps={{ width: "18", height: "18" }} />
                </Tooltip>
              </div>
            </div>
          )}
        </div>

        {/* Product Cards */}
        <ProductCardList
          className="flex flex-wrap sm:flex-nowrap gap-8 py-0 xl:basis-2/5"
          products={products}
          shouldScroll={false}
          cardFormatType={FormatType.Hero}
          cardFormatSubType={FormatSubType.HeroCard}
          location={eventing?.sectionLocation ?? "hero"}
        />
      </div>
    </div>
  );
}
